import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {EVENT_DETAILS} from "src/app/app-routing.module";
import {buildEventSlug} from "@utility";
import {environment} from "@environment";
import {PagingComponent} from "../../../components/paging.component";
import {
  CalendarViewType,
  EventFilter,
  EventItem,
  EventItemType,
  FindEventsRequest,
  LengthRange,
  ListSortOrder,
  Lookups,
  Message,
  Sport,
  Waypoint,
  PublicSearchFilter
} from "@model";
import {PublicEventsService} from "@services";
import {NgbDate} from '@ng-bootstrap/ng-bootstrap';
import {getDateFromNgbDate} from "@utility";
import {Observable} from "rxjs";
import {Meta, Title} from "@angular/platform-browser";

@Component({
  selector: 'app-event-calendar',
  templateUrl: './event-calendar.component.html',
  styleUrls: ['./event-calendar.component.scss'],
  standalone: false
})
export class EventCalendarComponent extends PagingComponent<EventItem> implements OnInit, OnDestroy {
  static DEFAULT_PAGE_SIZE: number = 51;

  @Input() countries: Lookups[] = [];
  @Input() sports: Sport[] = [];
  @Input() sportsForSearch: Sport[] = [];
  @Input() countriesForSearch: Lookups[] = [];
  @Input() listOfEvents: EventItem[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @Input() favoriteItems: EventItem[] = [];
  @Input() unreadMessages?: Message[];

  calendarType: EventItemType = EventItemType.UPCOMING;


  searchFilter: PublicSearchFilter = {};


  initialDate: NgbDate;

  lengthRanges: LengthRange[] = EventFilter.LENGTH_RANGES;
  environment: any = environment;
  lat: number = environment.headquartersLat;
  lng: number = environment.headquartersLng;
  waypointList: Waypoint[] = [];
  withBackground: boolean = false;

  constructor(private router: Router,
              private metaService: Meta,
              private titleService: Title,
              private publicEventsService: PublicEventsService,
              private route: ActivatedRoute) {
    super();
    this.PAGE_SIZE = EventCalendarComponent.DEFAULT_PAGE_SIZE;
    const now = new Date();
    this.initialDate = new NgbDate(now.getFullYear(), now.getMonth() + 1, now.getDate());
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.setSEOData();
    this.setComponentState(this.route);
    this.handleExploreResult(this.listOfEvents);
  }


  setSEOData(): void {
    const origin = this.document.location.origin;
    const url = `${origin}${this.document.location.pathname}`;
    const description = 'Find the race that inspires you to move';
    const heroImage = `${origin}/assets/img/hero2-mobile.webp`;
    const isFinished = this.route.snapshot.data['calendarType'] === EventItemType.FINISHED;
    const pageTitle = isFinished ? `Finished Events | ${environment.site_name}` : `Event Calendar | ${environment.site_name}`;

    this.titleService.setTitle(pageTitle);
    this.metaService.updateTag({name: 'description', content: description});

    this.metaService.updateTag({property: 'og:url', content: url});
    this.metaService.updateTag({property: 'og:title', content: environment.site_name});
    this.metaService.updateTag({property: 'og:type', content: 'website'});
    this.metaService.updateTag({property: 'og:site_name', content: environment.site_name});
    this.metaService.updateTag({property: 'og:description', content: description});
    this.metaService.updateTag({property: 'og:image', content: heroImage});

    this.metaService.updateTag({name: 'twitter:card', content: 'summary_large_image'});
    this.metaService.updateTag({name: 'twitter:title', content: environment.site_name});
    this.metaService.updateTag({name: 'twitter:description', content: description});
    this.metaService.updateTag({name: 'twitter:image', content: heroImage});

    this.setCanonicalUrl(url);
  }

  private setCanonicalUrl(url: string): void {
    let link = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!link) {
      link = this.document.createElement('link') as HTMLLinkElement;
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private setComponentState(activatedRoute: ActivatedRoute | null) {
    this.calendarType = activatedRoute?.snapshot.data['calendarType'];
  }



  protected fetchPage(page: number): Observable<EventItem[]> {
    const method =
      this.calendarType === EventItemType.UPCOMING
        ? 'findEventCalendarList'
        : 'filterFinishedEventsList';

    return this.publicEventsService[method](this.createFilter());
  }

  protected override afterSetItems(items: EventItem[]): void {
    this.listOfEvents = items;
    if (this.calendarType === EventItemType.UPCOMING) {
      this.findWayPointList();
    }
  }


  findWayPointList(): void {
    this.waypointList = this.listOfEvents
      .filter(event => event.latitude && event.longitude)
      .map(event => ({
        latitude: event.latitude,
        longitude: event.longitude,
        city: event.city,
        country: event.country,
        pointNameEn: event.nameEng,
        pointNameHr: event.name,
        pointNameIt: event.nameIta,
        pointNameSlo: event.nameSlo,
        eventItem: event
      }));
  }


  createFilter(): FindEventsRequest {
    return FindEventsRequest.createEventPageFilter(
      this.page,
      {
        ...this.searchFilter,
        ...this.getEventFilterDates()
      },
      this.calendarType === EventItemType.FINISHED ? ListSortOrder.ASC : ListSortOrder.DESC,
      EventCalendarComponent.DEFAULT_PAGE_SIZE
    );
  }

  getEventFilterDates(): {
    dateTo: Date | undefined,
    dateFrom: Date | undefined,
  } {
    if (EventItemType.FINISHED === this.calendarType) {
      return {
        dateFrom: this.searchFilter.dateFrom ?? undefined,
        dateTo: this.searchFilter.dateTo ?? getDateFromNgbDate(this.initialDate),
      }
    } else {
      return {
        dateTo: this.searchFilter.dateTo ?? undefined,
        dateFrom: this.searchFilter.dateFrom ?? getDateFromNgbDate(this.initialDate),
      }
    }
  }

  goToEventDetailsPage(item: EventItem): void {
    this.router.navigate([EVENT_DETAILS, buildEventSlug(item.id!, item)], {
      relativeTo: this.route,
    });
  }

  trackByFn(index: number, item: EventItem) {
    return item.id;
  }


  filterChanged(filter: PublicSearchFilter) {
    this.searchFilter = filter;
    this.onExplore(false);
  }


  protected readonly EventItemType = EventItemType;
  protected readonly CalendarViewType = CalendarViewType;
}

